/**
 * Inbox — the triage queue for invoices and Leistungsnachweise that arrive by
 * email. It sits BEFORE the invoice database: nothing is booked until the user
 * decides what to do with it.
 */

export type InboxItemKind = 'invoice' | 'service_description' | 'enclosure' | 'other'

export type InboxItemState =
  | 'new'
  | 'sent_to_flixpector'
  | 'returned_from_flixpector'
  | 'accepted'
  | 'dismissed'

/**
 * The inbox is a work queue, so only the states that still need a decision get
 * a tab. `accepted` lives on the Database page and `dismissed` is archived —
 * both leave the queue rather than sitting in it.
 */
export const INBOX_QUEUE_STATES: InboxItemState[] = [
  'new',
  'sent_to_flixpector',
  'returned_from_flixpector',
]

export type EnclosureReason =
  | 'recipient_is_submitting_firm' // addressed to the sending firm / issuer of the main invoice -> confidence high
  | 'recipient_not_flix' // addressed to someone who is not Flix or a subsidiary -> confidence normal
  | 'filename' // name matches ENCLOSURE_NAME (extra signal)
  | 'main_invoice_in_mail' // same mail has a Flix-addressed invoice from another supplier (extra signal)
  | 'manual' // marked by hand, no rule fired

/** 'high' only via recipient_is_submitting_firm. */
export type EnclosureConfidence = 'high' | 'normal'

/** Minimal view of a sibling item, enough for a list entry and the PDF link. */
export interface InboxItemRef {
  id: string
  kind: InboxItemKind
  state: InboxItemState
  filename: string | null
  documentId: string | null
  mimeType: string | null
  supplierName: string | null
  invoiceNumber: string | null
  totalGross: number | null
  currency: string | null
  invoiceId: string | null
}

/** Derived on read - never stored. */
export interface EnclosureInfo {
  /** ordered: recipient reason first, then filename, then main_invoice_in_mail */
  reasons: EnclosureReason[]
  /** null = marked by hand, no rule fired */
  confidence: EnclosureConfidence | null
  /** invoice-kind items of the same mail (any state); [] = no main invoice in this mail */
  mainItems: InboxItemRef[]
}

export type FlixpectorKind = 'legal_audit' | 'service_description'

export interface InboxItem {
  id: string
  kind: InboxItemKind
  state: InboxItemState

  messageId: string
  fromAddress: string | null
  /** The law firm, recovered from the forwarded original */
  originalSender: string | null
  subject: string | null
  receivedAt: string | null
  bodyPreview: string | null

  documentId: string | null
  filename: string | null
  mimeType: string | null
  size: number | null

  supplierName: string | null
  invoiceNumber: string | null
  invoiceDate: string | null
  totalGross: number | null
  currency: string | null
  confidence: string | null
  warnings: string[] | null

  invoiceId: string | null
  /** FLIXALIO user who owns the review; set when handing over to Flixpector */
  assigneeUserId: string | null
  assigneeName: string | null
  assigneeInitials: string | null
  flixpectorId: string | null
  flixpectorKind: FlixpectorKind | null
  error: string | null

  /** Outcome pulled back from Flixpector */
  flixpectorStatus: string | null
  /** Flixpector's own Bearbeiter box — free text there, so it can diverge */
  flixpectorReviewer: string | null
  flixpectorRating: string | null
  /**
   * Flixpector's document-level risk score, 0–10. Higher means MORE risk — see
   * scoreMeta() below, which colours it inversely to a quality score.
   */
  flixpectorScore: number | null
  /** As invoiced */
  flixpectorTotalAmount: number | null
  /** As agreed with the law firm */
  flixpectorFinalAmount: number | null
  flixpectorSyncedAt: string | null

  createdAt: string
  updatedAt: string

  /** Derived on read: kind === 'enclosure' only. Optional - undecorated routes omit it. */
  enclosure?: EnclosureInfo | null
  /** Derived on read: enclosure items of the same mail, on invoice items. */
  enclosures?: InboxItemRef[]
}

export interface InboxListResponse {
  items: InboxItem[]
  pending: number
  /** Mailbox being polled, or null when ingestion is switched off */
  mailbox?: string | null
}

export interface SpreadsheetPreview {
  filename: string | null
  sheetNames: string[]
  sheetName: string
  rows: Array<Array<string | number | null>>
  truncated: boolean
  totalRows: number
}

/**
 * All labels in this module are English source strings that double as i18n
 * keys — the module is plain TypeScript, so the components run them through
 * `t()` when they render them.
 */
export const INBOX_KIND_LABELS: Record<InboxItemKind, string> = {
  invoice: 'Invoice',
  service_description: 'Service record',
  enclosure: 'Enclosure (expenses)',
  other: 'Mail without a document',
}

export const INBOX_KIND_COLORS: Record<InboxItemKind, 'primary' | 'secondary' | 'default'> = {
  invoice: 'primary',
  service_description: 'secondary',
  enclosure: 'default',
  other: 'default',
}

export const ENCLOSURE_REASON_LABELS: Record<EnclosureReason, string> = {
  recipient_is_submitting_firm: 'Addressed to the law firm that sent it',
  recipient_not_flix: 'Not addressed to Flix or a subsidiary',
  filename: 'File name points to an enclosure',
  main_invoice_in_mail: 'Same mail contains the main invoice',
  manual: 'Marked by hand',
}

export const ENCLOSURE_CONFIDENCE_LABELS: Record<EnclosureConfidence, string> = {
  high: 'Confident',
  normal: 'Please check',
}

export const INBOX_STATE_LABELS: Record<InboxItemState, string> = {
  new: 'Open',
  sent_to_flixpector: 'With Flixpector',
  returned_from_flixpector: 'Back from Flixpector',
  accepted: 'In the database',
  dismissed: 'Filed without a document',
}

/** Only spreadsheets need the server-side table preview; PDFs embed directly. */
export function isSpreadsheet(item: InboxItem): boolean {
  const mime = (item.mimeType || '').toLowerCase()
  return (
    mime.includes('spreadsheet') ||
    mime.includes('ms-excel') ||
    mime.includes('csv') ||
    /\.(xlsx|xlsm|xls|ods|csv)$/i.test(item.filename || '')
  )
}

export function isPdf(item: InboxItem): boolean {
  return (
    (item.mimeType || '').toLowerCase() === 'application/pdf' ||
    /\.pdf$/i.test(item.filename || '')
  )
}

/** Flixpector's workflow states, translated for display. */
export const FLIXPECTOR_STATUS_LABELS: Record<string, string> = {
  pending_review: 'Review open',
  pending: 'Review open',
  review: 'In review',
  clarification: 'In clarification with the law firm',
  approved: 'Approved',
  rejected: 'Rejected',
  done: 'Completed',
}

export const FLIXPECTOR_RATING_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  green: 'success',
  yellow: 'warning',
  red: 'error',
}

/* ------------------------------------------------------------------------- *
 * Presentation helpers — the design mockup's colour logic, one place.
 * ------------------------------------------------------------------------- */

/** Colour of the leading dot: Flixpector's verdict, else open warnings. */
export function ratingDot(item: InboxItem): string {
  if (item.flixpectorRating === 'green') return 'var(--ok)'
  if (item.flixpectorRating === 'yellow') return 'var(--warn)'
  if (item.flixpectorRating === 'red') return 'var(--err)'
  if (item.warnings && item.warnings.length > 0) return 'var(--warn)'
  if (item.state === 'accepted') return 'var(--ok)'
  return '#6b7a90'
}

export interface KindMeta {
  label: string
  fg: string
  bg: string
  border: string
}

export function kindMeta(kind: InboxItemKind): KindMeta {
  if (kind === 'invoice') {
    return {
      label: INBOX_KIND_LABELS.invoice,
      fg: 'var(--kInvFg)',
      bg: 'var(--kInvBg)',
      border: 'var(--kInvBd)',
    }
  }
  if (kind === 'service_description') {
    return {
      label: INBOX_KIND_LABELS.service_description,
      fg: 'var(--kSrvFg)',
      bg: 'var(--kSrvBg)',
      border: 'var(--kSrvBd)',
    }
  }
  if (kind === 'enclosure') {
    return {
      label: INBOX_KIND_LABELS.enclosure,
      fg: 'var(--warn)',
      bg: 'var(--kNeuBg)',
      border: 'var(--kNeuBd)',
    }
  }
  return {
    label: INBOX_KIND_LABELS.other,
    fg: 'var(--kNeuFg)',
    bg: 'var(--kNeuBg)',
    border: 'var(--kNeuBd)',
  }
}

/** Colours of the confidence pill; null (marked by hand) gets the neutral set. */
export function enclosureConfidenceMeta(c: EnclosureConfidence | null): {
  label: string | null
  fg: string
  bg: string
  border: string
} {
  return {
    label: c ? ENCLOSURE_CONFIDENCE_LABELS[c] : null,
    fg: c === 'high' ? 'var(--ok)' : 'var(--kNeuFg)',
    bg: 'var(--kNeuBg)',
    border: 'var(--kNeuBd)',
  }
}

/** The monospace file badge: PDF red, spreadsheets green, plain mail neutral. */
export function fileMeta(item: InboxItem): { tag: string; fg: string; bg: string } {
  if (isPdf(item)) return { tag: 'PDF', fg: 'var(--fPdfFg)', bg: 'var(--fPdfBg)' }
  if (isSpreadsheet(item)) {
    const ext = (item.filename || '').match(/\.(xlsx|xlsm|xls|ods|csv)$/i)?.[1]
    return { tag: (ext || 'XLSX').toUpperCase(), fg: 'var(--fShFg)', bg: 'var(--fShBg)' }
  }
  if (!item.documentId) return { tag: 'MAIL', fg: 'var(--fNeFg)', bg: 'var(--fNeBg)' }
  return { tag: 'DOC', fg: 'var(--fNeFg)', bg: 'var(--fNeBg)' }
}

/**
 * Flixpector's score, formatted for the list.
 *
 * NOTE the direction: this is a RISK score (0 = clean, 10 = very risky), so a
 * low number is the good one. That is the opposite of the design mockup, whose
 * placeholder data read as a quality score — hence the explicit "Risk" label
 * so a green 2,1 can never be mistaken for a bad grade.
 */
export function scoreMeta(item: InboxItem): {
  text: string
  fg: string
  /** i18n key; `{score}` is substituted by the caller after translation */
  title: string
  score: number | null
} {
  const score = item.flixpectorScore
  if (score == null) {
    return {
      text: '–/10',
      fg: 'var(--faint)',
      title: 'Flixpector has no risk score yet',
      score: null,
    }
  }
  const fg = score <= 3 ? 'var(--ok)' : score <= 6 ? 'var(--warn)' : 'var(--err)'
  return {
    text: `${score.toFixed(1).replace('.', ',')}/10`,
    fg,
    title: 'Flixpector risk score {score} out of 10 — higher means more risk',
    score,
  }
}

/** Confidence of the extraction, as a pill in the detail drawer. */
export function confidenceMeta(confidence: string | null): {
  label: string
  fg: string
  border: string
} {
  if (confidence === 'high') return { label: 'High confidence', fg: 'var(--ok)', border: 'var(--ok)' }
  if (confidence === 'medium') {
    return { label: 'Medium confidence', fg: 'var(--warn)', border: 'var(--warn)' }
  }
  if (confidence === 'low') {
    return { label: 'Low confidence', fg: 'var(--err)', border: 'var(--err)' }
  }
  return { label: 'No extraction', fg: 'var(--muted)', border: 'var(--line)' }
}
